import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useTheme } from "../Utils/useTheme";
import { useSEO } from "../Utils/useSEO";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import { UserPlus, CheckCircle2, ShieldCheck, ArrowLeft, ArrowRight, Cpu, Sparkles, Building, CheckCircle, Lock, Database, } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function SupplierOnboardingPage() {
  useSEO({
    title: "Supplier Onboarding Portal",
    description: "Access the Supply Spot onboarding pipeline to submit profile details, certifications, and compliance registers."
  });

  const { theme } = useTheme();
  const isDark = theme !== "light";
  // Simulator State
  const [currentStep, setCurrentStep] = useState(1);
  const [companyName, setCompanyName] = useState("Vanguard Logistics");
  const [country, setCountry] = useState("India");
  const [industry, setIndustry] = useState("Manufacturing");
  
  // Checklist verification state
  const [checklist, setChecklist] = useState({
    taxRegistry: true,
    isoCertificate: false,
    bankVerification: false,
    esgCode: true,
  });

  // AI Audit log lines state
  const [auditProgress, setAuditProgress] = useState(0);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    if (currentStep === 3) {
      setAuditProgress(0);
      setAuditLogs([]);
      
      const logs = [
        "Initializing Aether Core verification protocol...",
        `[OK] Tax ID pattern match confirmed for ${country} registry.`,
        checklist.isoCertificate 
          ? "[OK] ISO Quality Certificate verified from international registrar." 
          : "[WARN] ISO Quality Certificate missing. flag logged in risk score.",
        checklist.bankVerification
          ? "[OK] Bank ledger escrow account handshake completed."
          : "[WARN] Bank account escrow validation incomplete. Flag logged.",
        checklist.esgCode
          ? "[OK] ESG Code of Conduct digital acknowledgment verified."
          : "[WARN] ESG compliance acknowledgment missing.",
        "Synthesizing composite risk scorecard...",
        "Registering cryptographic supplier node ID...",
        "Bi-directional replication sync with SAP (S/4HANA) initiated...",
        "Pipeline audit complete. Registry status: ready."
      ];

      let logIndex = 0;
      const interval = setInterval(() => {
        if (logIndex < logs.length) {
          const nextLog = logs[logIndex];
          setAuditLogs(prev => [...prev, nextLog]);
          setAuditProgress(Math.round(((logIndex + 1) / logs.length) * 100));
          logIndex++;
        } else {
          clearInterval(interval);
        }
      }, 700);

      return () => clearInterval(interval);
    }
  }, [currentStep, country, checklist]);

  const toggleChecklist = (key) => {
    setChecklist(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getRiskScore = () => {
    let score = 100;
    if (!checklist.isoCertificate) score -= 15;
    if (!checklist.bankVerification) score -= 25;
    if (!checklist.esgCode) score -= 10;
    return score;
  };

  const pillars = [
    {
      icon: <Building className="w-5 h-5 text-brand-orange" />,
      title: "Self-Service Portal",
      desc: "Suppliers easily input operational metadata, banking details, and ESG credentials independently, bypassing administrative overhead."
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-brand-amber" />,
      title: "Automated Verification Gates",
      desc: "Direct integration with global corporate databases, tax registers, and sanction lists checks details in real-time."
    },
    {
      icon: <Cpu className="w-5 h-5 text-brand-gold" />,
      title: "Configurable Risk Scoring",
      desc: "Calculates instant risk profiles and compliance scores based on document accuracy, ESG reviews, and background validation logs."
    },
    {
      icon: <Database className="w-5 h-5 text-brand-orange" />,
      title: "ERP Ledger Replication",
      desc: "Validated vendor accounts replicate instantly into central ERP arrays like SAP (S/4HANA) and Oracle with zero manual transcription."
    }
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Background Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Ambient Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/5 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-brand-amber/5 rounded-full blur-[140px] -z-10 pointer-events-none" />

      <main className="max-w-7xl mx-auto px-6 pt-36 pb-24 relative z-10">
        
        {/* Breadcrumb and Back Action */}
        <ScrollReveal className="self-start text-left mb-8">
          <Link
            to="/features"
            className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary hover:text-brand-orange transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Capabilities</span>
          </Link>
        </ScrollReveal>

        {/* HERO SECTION */}
        <section className="flex flex-col lg:flex-row items-center gap-12 mb-24">
          <ScrollReveal className="flex-1 text-left flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider self-start shadow-md">
              <Sparkles className="w-3.5 h-3.5" />
              <span>MODULE 01. DETAILED CAPABILITY</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-primary leading-none font-title">
              Supplier <br />
              <span className="bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-md">
                Onboarding Portal
              </span>
            </h1>

            <p className="text-text-secondary text-sm md:text-base leading-relaxed max-w-xl">
              Turn manual compliance reviews and slow document checks into a self-service pipeline. Validate vendor tax registries, bank details, and ESG documents in real-time, matching details automatically before logistics dispatch begins.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-2">
              <button
                onClick={() => {
                  const el = document.getElementById("simulator-section");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-6 py-3.5 font-bold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                RUN PIPELINE SIMULATOR
              </button>
              <Link
                to="/contact"
                className={`px-6 py-3.5 font-bold text-xs tracking-wider rounded-lg bg-transparent border transition-all duration-300 ${isDark
                    ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                    : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                  }`}
              >
                REQUEST CUSTOM ERP BUILD
              </Link>
            </div>
          </ScrollReveal>

          {/* Right Hero Graphic */}
          <ScrollReveal delay={0.15} className="flex-1 w-full h-[320px]">
            <div className="w-full h-full glass-panel rounded-3xl relative overflow-hidden shadow-2xl flex flex-col justify-center p-8 text-left border border-brand-orange/15 bg-gradient-to-tr from-bg-secondary/40 via-bg-primary/20 to-bg-secondary/10">
              <div className="absolute top-4 left-4 flex items-center gap-2 px-2.5 py-1 bg-bg-primary/80 border border-brand-orange/20 rounded-full">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
                <span className="text-[9px] font-title font-bold text-text-primary tracking-wider uppercase">
                  AETHER VERIFICATION FLOW
                </span>
              </div>

              <div className="flex flex-col gap-4 mt-6">
                <div className="p-4 bg-bg-secondary/60 border border-text-muted/10 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UserPlus className="w-5 h-5 text-brand-orange" />
                    <div>
                      <div className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">ONBOARDING PIPELINE</div>
                      <div className="text-xs font-bold text-text-primary">Portal Link Sent to Supplier</div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded border border-brand-orange/20">ACTIVE</span>
                </div>

                <div className="p-4 bg-bg-secondary/60 border border-text-muted/10 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-brand-amber" />
                    <div>
                      <div className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">DIAGNOSTICS & SYNC</div>
                      <div className="text-xs font-bold text-text-primary">Government Registry Tax Match</div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">CONFIRMED</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </section>

        {/* CORE PILLARS SECTION */}
        <section className="mb-24">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold font-title text-text-primary mb-4">
              Structural Pillars of Automated Onboarding
            </h2>
            <p className="text-text-secondary text-xs sm:text-sm">
              We design our self-service portal to completely bypass manual screening workflows.
            </p>
          </ScrollReveal>

          <ScrollReveal staggerChildren={0.06} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, idx) => (
              <ScrollRevealItem key={idx} className="flex">
                <div className="glass-panel p-6 rounded-2xl border border-brand-orange/10 hover:border-brand-amber/35 transition-all duration-300 flex flex-col gap-4 text-left w-full relative group">
                  {/* <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-orange to-brand-amber opacity-0 group-hover:opacity-100 transition-opacity" /> */}
                  <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange">
                    {pillar.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-text-primary mb-2">{pillar.title}</h3>
                    <p className="text-text-secondary text-xs leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              </ScrollRevealItem>
            ))}
          </ScrollReveal>
        </section>

        {/* INTERACTIVE ONBOARDING SIMULATOR */}
        <section id="simulator-section" className="mb-24 border-y border-border-theme py-20 bg-bg-secondary/40">
          <div className="max-w-4xl mx-auto">
            
            <ScrollReveal className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
                <Cpu className="w-3.5 h-3.5" />
                <span>ONBOARDING SANDBOX</span>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-text-primary font-title">
                Interactive Onboarding Pipeline
              </h2>
              <p className="text-text-secondary text-xs sm:text-sm">
                Run our simulated compliance audit tool. Step through configuration settings, input supplier details, check validation grids, and trigger node sync.
              </p>
            </ScrollReveal>

            <ScrollReveal className="glass-panel p-8 shadow-2xl relative border border-brand-orange/15 rounded-3xl">
              
              {/* Simulator Steps Nav */}
              <div className="flex justify-between items-center mb-10 pb-6 border-b border-border-theme">
                {[
                  { step: 1, label: "Profile" },
                  { step: 2, label: "Checklist" },
                  { step: 3, label: "Audit Logs" },
                  { step: 4, label: "Registry Entry" }
                ].map((s) => (
                  <div key={s.step} className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all ${
                        currentStep === s.step
                          ? "bg-brand-orange text-white shadow-glow-orange scale-105"
                          : currentStep > s.step
                          ? "bg-emerald-500 text-white"
                          : "bg-bg-secondary border border-border-theme text-text-secondary"
                      }`}
                    >
                      {currentStep > s.step ? <CheckCircle className="w-4 h-4" /> : s.step}
                    </div>
                    <span
                      className={`text-xs font-bold font-title tracking-wider hidden sm:inline ${
                        currentStep === s.step ? "text-brand-orange" : "text-text-secondary"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Steps Body */}
              <div className="min-h-[250px] flex flex-col justify-between">
                <AnimatePresence mode="wait">
                  
                  {/* Step 1: Profile Details */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex flex-col gap-6 text-left"
                    >
                      <div>
                        <h3 className="font-bold text-sm text-text-primary mb-1">Step 1: Supplier Profile Identification</h3>
                        <p className="text-xs text-text-secondary">Input details for the simulated supplier profile registry.</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs text-text-secondary font-semibold">Supplier/Company Name</label>
                          <input
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-xs text-text-primary focus:outline-none"
                            placeholder="Vanguard Logistics"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs text-text-secondary font-semibold">Origin/Region</label>
                          <select
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-xs text-text-primary focus:outline-none"
                          >
                            <option value="India">India (GST Registry)</option>
                            <option value="Singapore">Singapore (UEN Sync)</option>
                            <option value="UAE">UAE (DED License Sync)</option>
                            <option value="USA">USA (EIN Tax Sync)</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-text-secondary font-semibold">Industry Focus</label>
                        <select
                          value={industry}
                          onChange={(e) => setIndustry(e.target.value)}
                          className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-xs text-text-primary focus:outline-none w-full"
                        >
                          <option value="Manufacturing">Manufacturing (Heavy Industrial)</option>
                          <option value="Logistics">Logistics (Freight & Yard Operations)</option>
                          <option value="Retail">Retail & Consumer Goods</option>
                          <option value="Technology">Technology & Hardware Hardware</option>
                        </select>
                      </div>

                      <button
                        onClick={() => setCurrentStep(2)}
                        disabled={!companyName.trim()}
                        className="mt-4 px-6 py-3 bg-brand-orange hover:bg-brand-amber font-bold text-xs tracking-wider rounded-lg text-white transition-all flex items-center gap-1.5 disabled:opacity-50 shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30"
                      >
                        <span>Configure Compliance Checklist</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}

                  {/* Step 2: Verification Checklist */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex flex-col gap-6 text-left"
                    >
                      <div>
                        <h3 className="font-bold text-sm text-text-primary mb-1">Step 2: Compliance Verification Parameters</h3>
                        <p className="text-xs text-text-secondary">Select which checks Aether Core will run. Toggle checklist flags to check risk score updates.</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { key: "taxRegistry", label: "Govt Tax Sync (Mandatory)", desc: "Confirms valid registry credentials." },
                          { key: "isoCertificate", label: "ISO 9001 Certification", desc: "Confirms active certification registers." },
                          { key: "bankVerification", label: "AP Bank Account handshake", desc: "Handshake validates ledger nodes." },
                          { key: "esgCode", label: "ESG Acknowledgment", desc: "Confirms carbon code of conduct sign." }
                        ].map((chk) => (
                          <div
                            key={chk.key}
                            onClick={() => toggleChecklist(chk.key)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all ${
                              checklist[chk.key]
                                ? "bg-brand-orange/5 border-brand-orange/50 shadow-md"
                                : "bg-bg-secondary/40 border-border-theme hover:border-brand-orange/30"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-4 h-4 rounded border flex items-center justify-center ${
                                  checklist[chk.key]
                                    ? "bg-brand-orange border-brand-orange text-white"
                                    : "border-border-theme"
                                }`}
                              >
                                {checklist[chk.key] && <CheckCircle className="w-3.5 h-3.5 text-bg-primary" />}
                              </div>
                              <div>
                                <div className="text-xs font-bold text-text-primary">{chk.label}</div>
                                <div className="text-[10px] text-text-secondary leading-relaxed">{chk.desc}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="p-4 bg-bg-secondary/50 border border-border-theme rounded-xl flex justify-between items-center">
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">PROJECTED RISK SCORE</div>
                          <div className="text-xs font-bold text-text-primary leading-none mt-1">Calculated Compliance Factor</div>
                        </div>
                        <div className={`text-xl font-black font-title ${
                          getRiskScore() >= 80 ? "text-emerald-500" : getRiskScore() >= 60 ? "text-brand-amber" : "text-brand-orange"
                        }`}>
                          {getRiskScore()} / 100
                        </div>
                      </div>

                      <div className="flex justify-between mt-4">
                        <button
                          onClick={() => setCurrentStep(1)}
                          className={`px-5 py-3 flex items-center gap-2 border transition-all duration-300 font-bold text-xs tracking-wider rounded-lg ${isDark
                              ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                              : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                            }`}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span>Previous Step</span>
                        </button>
                        <button
                          onClick={() => setCurrentStep(3)}
                          className="px-6 py-3 bg-brand-orange hover:bg-brand-amber font-bold text-xs tracking-wider rounded-lg text-white hover:brightness-110 transition-all flex items-center gap-1.5 shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30"
                        >
                          <span>Execute AI Audit Pipeline</span>
                          <Cpu className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: AI Audit Logs */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex flex-col gap-6 text-left"
                    >
                      <div>
                        <h3 className="font-bold text-sm text-text-primary mb-1">Step 3: Aether Core Integrity Pipeline</h3>
                        <p className="text-xs text-text-secondary">Watch real-time verification adapters audit data nodes.</p>
                      </div>

                      {/* Audit Log Box */}
                      <div className="bg-slate-950 border border-brand-orange/20 rounded-xl p-4 font-mono text-[10px] text-slate-300 min-h-[160px] flex flex-col gap-2 overflow-y-auto max-h-[220px]">
                        {auditLogs.map((log, idx) => (
                          <div key={idx} className="flex gap-2">
                            <span className="text-brand-orange font-bold">&gt;</span>
                            <span className={log.includes("[WARN]") ? "text-brand-amber font-semibold" : log.includes("[OK]") ? "text-emerald-500" : ""}>
                              {log}
                            </span>
                          </div>
                        ))}
                        {auditProgress < 100 && (
                          <div className="flex items-center gap-1.5 text-brand-orange animate-pulse">
                            <span>●</span>
                            <span>Adapters scanning registers...</span>
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-bg-secondary rounded-full h-2 overflow-hidden border border-border-theme">
                        <div
                          className="bg-gradient-to-r from-brand-orange to-brand-amber h-full transition-all duration-500"
                          style={{ width: `${auditProgress}%` }}
                        />
                      </div>

                      <div className="flex justify-between items-center mt-4">
                        <span className="text-[10px] font-bold text-text-secondary">Pipeline Progress: {auditProgress}%</span>
                        <button
                          onClick={() => setCurrentStep(4)}
                          disabled={auditProgress < 100}
                          className="px-6 py-3 bg-brand-orange hover:bg-brand-amber font-bold text-xs tracking-wider rounded-lg text-white hover:brightness-110 disabled:opacity-50 transition-all flex items-center gap-1.5 shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30"
                        >
                          <span>Generate Registry Entry</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: Registry Entry Generated */}
                  {currentStep === 4 && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="flex flex-col items-center text-center gap-6"
                    >
                      {/* Badge Display */}
                      <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center text-emerald-500 shadow-lg shadow-emerald-500/10 animate-bounce">
                        <CheckCircle2 className="w-10 h-10" />
                      </div>

                      <div className="text-center">
                        <h3 className="font-bold text-lg text-text-primary mb-1">Supplier Node Successfully Registered</h3>
                        <p className="text-xs text-text-secondary max-w-md mx-auto">
                          {companyName} compliance record validation succeeded. Encrypted record sync is active.
                        </p>
                      </div>

                      {/* Diagnostic Ledger Summary */}
                      <div className="w-full max-w-md bg-bg-secondary/40 border border-border-theme p-5 rounded-2xl text-left grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">SUPPLIER REGISTRY ID</div>
                          <div className="text-xs font-bold text-text-primary mt-0.5 font-mono">SUPP-NODE-83A41</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">COMPLIANCE STATE</div>
                          <div className={`text-xs font-bold mt-0.5 flex items-center gap-1 ${getRiskScore() >= 80 ? "text-emerald-500" : "text-brand-amber"}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {getRiskScore() >= 80 ? "Active & Verified" : "Active with Warnings"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">ESCROW STATE</div>
                          <div className="text-xs font-bold text-text-primary mt-0.5 flex items-center gap-1">
                            <Lock className="w-3 h-3 text-emerald-500" />
                            Ready for PO Releases
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">ERP STATE</div>
                          <div className="text-xs font-bold text-text-primary mt-0.5 flex items-center gap-1.5">
                            <Database className="w-3 h-3 text-brand-orange" />
                            Replicated in SAP S/4HANA
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setCurrentStep(1);
                          setChecklist({
                            taxRegistry: true,
                            isoCertificate: false,
                            bankVerification: false,
                            esgCode: true,
                          });
                        }}
                        className={`px-6 py-2.5 bg-transparent border transition-all duration-300 rounded-xl text-xs font-bold ${isDark
                            ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                            : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                          }`}
                      >
                        Reset Simulator Pipeline
                      </button>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>

            </ScrollReveal>
          </div>
        </section>

        {/* ENTERPRISE ERP CAPABILITIES DETAILS */}
        <section className="max-w-4xl mx-auto mb-20 text-left flex flex-col gap-8">
          <ScrollReveal>
            <h2 className="text-2xl sm:text-3xl font-bold font-title mb-6">Detailed Onboarding Architecture</h2>
            <p className="text-text-secondary text-xs sm:text-sm leading-relaxed mb-6">
              When a new supplier registers inside the self-service onboarding portal, Aether Core starts verification pipelines in the background. If compliance variables fail (e.g. document expiration flags, tax identification conflicts), automated warnings flag the profile instantly inside the procure-to-pay control deck.
            </p>
          </ScrollReveal>

          <ScrollReveal staggerChildren={0.06} className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <ScrollRevealItem className="glass-panel p-6 rounded-xl border border-border-theme flex flex-col gap-3">
              <span className="w-8 h-8 rounded-lg bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange text-xs font-bold">01</span>
              <h4 className="font-bold text-xs text-text-primary uppercase tracking-wider">Sanction Logs scan</h4>
              <p className="text-text-secondary text-[11px] leading-relaxed">Continuous screening checks profiles against OFAC, EU, and global trade sanction registries.</p>
            </ScrollRevealItem>

            <ScrollRevealItem className="glass-panel p-6 rounded-xl border border-border-theme flex flex-col gap-3">
              <span className="w-8 h-8 rounded-lg bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange text-xs font-bold">02</span>
              <h4 className="font-bold text-xs text-text-primary uppercase tracking-wider">Escrow bank sync</h4>
              <p className="text-text-secondary text-[11px] leading-relaxed">Validates target bank routing nodes to execute digital escrow release patterns automatically.</p>
            </ScrollRevealItem>

            <ScrollRevealItem className="glass-panel p-6 rounded-xl border border-border-theme flex flex-col gap-3">
              <span className="w-8 h-8 rounded-lg bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange text-xs font-bold">03</span>
              <h4 className="font-bold text-xs text-text-primary uppercase tracking-wider">ISO Expiry alerts</h4>
              <p className="text-text-secondary text-[11px] leading-relaxed">Tracks validity dates on ISO certificates and flags procurement teams 30 days before expiration.</p>
            </ScrollRevealItem>
          </ScrollReveal>
        </section>

        {/* FINAL CTA SECTION */}
        <ScrollReveal>
          <div className="glass-panel p-10 md:p-12 rounded-3xl border border-brand-orange/15 bg-gradient-to-r from-bg-secondary/40 to-bg-primary relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 mt-20">
            <div className="max-w-xl text-left">
              <h2 className="text-xl md:text-2xl font-title font-bold mb-3">Integrate with your procurement core?</h2>
              <p className="text-text-secondary text-xs md:text-sm leading-relaxed">
                Empower your sourcing team by integrating our self-service validation layers into your existing SAP instance or proprietary database.
              </p>
            </div>
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 whitespace-nowrap"
            >
              SCHEDULE INTEGRATION WALKTHROUGH
            </Link>
          </div>
        </ScrollReveal>

      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
